import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { DEVICE_WIDTH, previewColors, SYSTEM_FONT_STACK } from './previewTheme'

const t = buildTranslate('live_updates.preview')

const CLOCK_FONT_SIZE = '60px'
const CARDS_GAP = '12px'

export interface AndroidLockScreenProps {
  children: React.ReactNode
}

// Android lock-screen frame — parallel in structure to IosLockScreen but with
// the lighter treatment from the Figma Android column: a lighter teal
// wallpaper, a 9:30 clock, and (via the hosted cards) white notification cards
// with dark text. All off-theme literals are annotated in previewTheme.
const AndroidLockScreen = ({ children }: AndroidLockScreenProps) => {
  const [css] = useStyletron()

  return (
    <div
      role="group"
      aria-label={t('android_frame_label')}
      className={css({
        width: DEVICE_WIDTH,
        maxWidth: '100%',
        borderTopLeftRadius: '24px',
        borderTopRightRadius: '24px',
        border: `8px solid ${previewColors.deviceBezel}`,
        borderBottom: 'none',
        backgroundColor: previewColors.androidWallpaper,
        fontFamily: SYSTEM_FONT_STACK,
        paddingTop: '12px',
        paddingBottom: '32px',
        display: 'flex',
        flexDirection: 'column',
      })}
    >
      {/* Android status bar. Glyphs are decorative CSS shapes. */}
      <div
        aria-hidden="true"
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '6px',
          padding: '0 20px',
          height: '20px',
        })}
      >
        {/* Cellular signal bars */}
        <div
          className={css({
            display: 'flex',
            alignItems: 'flex-end',
            gap: '2px',
          })}
        >
          {['4px', '6px', '8px', '10px'].map(height => (
            <div
              key={height}
              className={css({
                width: '3px',
                height,
                borderRadius: '1px',
                backgroundColor: palette.white,
              })}
            />
          ))}
        </div>
        {/* Wi-Fi (quarter-circle wedge) */}
        <div
          className={css({
            width: '14px',
            height: '10px',
            borderTopLeftRadius: '14px',
            borderTopRightRadius: '14px',
            backgroundColor: palette.white,
          })}
        />
        {/* Battery */}
        <div
          className={css({
            width: '22px',
            height: '11px',
            borderRadius: '3px',
            border: `1px solid ${palette.white}`,
            padding: '1px',
          })}
        >
          <div
            className={css({
              width: '100%',
              height: '100%',
              borderRadius: '1px',
              backgroundColor: palette.white,
            })}
          />
        </div>
      </div>

      {/* Big clock (Android lock screens lead with the time, no date line). */}
      <div
        className={css({
          display: 'flex',
          justifyContent: 'center',
          marginTop: '28px',
          marginBottom: '28px',
        })}
      >
        <div
          className={css({
            color: palette.white,
            fontFamily: SYSTEM_FONT_STACK,
            fontSize: CLOCK_FONT_SIZE,
            fontWeight: 400,
            lineHeight: '1.1',
          })}
        >
          {t('android_time')}
        </div>
      </div>

      <div
        className={css({
          padding: '0 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: CARDS_GAP,
        })}
      >
        {children}
      </div>
    </div>
  )
}

export default AndroidLockScreen
